/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  Empty,
  message,
  Modal,
  Popover,
  Tag,
  Tooltip,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  ProfileOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import IconButton from '~components/_common/IconButton/IconButton';
import { OffersScreeningRow } from './OffersScreeningRow';
import {
  buildMergedScreeningVouchers,
  getOfferScreeningValue,
  getOfferVendorName,
  ScreeningContractLike,
  ScreeningEvaluationValue,
  ScreeningOfferData,
  ScreeningOfferLike,
  ScreeningVoucherLike,
  ScreeningVoucherResponseLike,
} from './screeningDemoData';
import {
  computeOfferSerials,
  getOfferSerial,
} from '../OffersEvaluation/evaluationData';
import {
  BulkOfferEntry,
  downloadAllOffersZip,
} from '~src/utils/downloader/downloadOfferZip';
import { useStore, DownloadingState } from '~src/store';
import {
  acceptAllContractScreeningOffers,
  finalizeContractScreening,
  setContractOfferScreeningEvaluationRejected,
  setContractOfferScreeningEvaluationSelected,
  skipRemainingContractScreening,
} from '~src/services/api/offers';
import { queryCache, useMutation } from 'react-query';

type OffersScreeningViewProps = {
  projectId: number;
  tenderId: number;
  contract: ScreeningContractLike;
  offers: ScreeningOfferLike[];
  vouchers: ScreeningVoucherLike[];
  voucherResponsesByOfferId?: Record<number, ScreeningVoucherResponseLike[]>;
  offerDataById?: Record<number, ScreeningOfferData>;
  isScreeningLocked?: boolean;
};

type DeliveryFilterValue = 'undecided' | 'accepted' | 'rejected';

const DELIVERY_FILTER_VALUES: DeliveryFilterValue[] = [
  'undecided',
  'accepted',
  'rejected',
];

// Stable empty reference so rows without responses keep referential equality
// (otherwise a new [] every render would defeat React.memo on the row).
const EMPTY_RESPONSES: ScreeningVoucherResponseLike[] = [];

const SCREENING_DELIVERY_FILTER_OPTIONS: Array<{
  intlId: string;
  value: DeliveryFilterValue;
  color: string;
  icon: ReactNode;
}> = [
  {
    intlId: 'screening-filter-undecided',
    value: 'undecided',
    color: '#8c8c8c',
    icon: <ClockCircleOutlined />,
  },
  {
    intlId: 'screening-filter-accepted',
    value: 'accepted',
    color: '#52c41a',
    icon: <CheckCircleOutlined />,
  },
  {
    intlId: 'screening-filter-rejected',
    value: 'rejected',
    color: '#ff4d4f',
    icon: <StopOutlined />,
  },
];

const getDeliveryFilterValue = (
  screeningValue: ScreeningEvaluationValue
): DeliveryFilterValue => {
  switch (screeningValue) {
    case 3:
      return 'accepted';
    case 4:
      return 'rejected';
    default:
      return 'undecided';
  }
};

const isScreeningDecisionSelected = (
  screeningValue: ScreeningEvaluationValue
) => {
  return screeningValue === 3 || screeningValue === 4;
};

export const OffersScreeningView: FC<OffersScreeningViewProps> = ({
  projectId,
  tenderId,
  contract,
  offers,
  vouchers,
  voucherResponsesByOfferId = {},
  offerDataById,
  isScreeningLocked,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const [filterValues, setFilterValues] = useState<DeliveryFilterValue[]>(
    DELIVERY_FILTER_VALUES
  );
  const [screeningStateByOfferId, setScreeningStateByOfferId] = useState<
    Record<number, ScreeningEvaluationValue>
  >({});
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  // Mirror state in a ref so the change handler can stay referentially stable
  // (it reads the previous value without depending on the state map).
  const screeningStateRef = useRef(screeningStateByOfferId);
  screeningStateRef.current = screeningStateByOfferId;

  const invalidateScreeningQueries = () => {
    queryCache.invalidateQueries();
  };

  const [setScreeningEvaluationMutation] = useMutation(
    ({
      offerId,
      value,
    }: {
      offerId: number;
      value: ScreeningEvaluationValue;
    }) => {
      const params = {
        projectId,
        tenderId,
        contractId: contract.id,
        offerId,
      };

      if (value === 3) {
        return setContractOfferScreeningEvaluationSelected(params);
      }

      if (value === 4) {
        return setContractOfferScreeningEvaluationRejected(params);
      }

      return Promise.reject();
    }
  );

  const [
    finalizeScreeningMutation,
    { isLoading: isFinalizingScreening },
  ] = useMutation(
    () =>
      finalizeContractScreening({
        projectId,
        tenderId,
        contractId: contract.id,
      }),
    {
      onSuccess: () => {
        invalidateScreeningQueries();
      },
    }
  );

  const [
    skipScreeningMutation,
    { isLoading: isSkippingScreening },
  ] = useMutation(
    () =>
      skipRemainingContractScreening({
        projectId,
        tenderId,
        contractId: contract.id,
      }),
    {
      onSuccess: () => {
        invalidateScreeningQueries();
      },
    }
  );

  const [
    acceptAllScreeningOffersMutation,
    { isLoading: isAcceptingAllScreeningOffers },
  ] = useMutation(
    () =>
      acceptAllContractScreeningOffers({
        projectId,
        tenderId,
        contractId: contract.id,
      }),
    {
      onSuccess: () => {
        invalidateScreeningQueries();
      },
    }
  );

  const isScreeningActionLoading =
    isFinalizingScreening ||
    isSkippingScreening ||
    isAcceptingAllScreeningOffers;

  const isAllFiltersSelected =
    filterValues.length === DELIVERY_FILTER_VALUES.length;

  const filterButtonLabel = useMemo(() => {
    if (isAllFiltersSelected) {
      return intl.formatMessage({ id: 'screening-filter-all-deliveries' });
    }

    if (!filterValues.length) {
      return intl.formatMessage({ id: 'screening-filter-no-filters-selected' });
    }

    return SCREENING_DELIVERY_FILTER_OPTIONS.filter((option) =>
      filterValues.includes(option.value)
    )
      .map((option) => intl.formatMessage({ id: option.intlId }))
      .join(', ');
  }, [filterValues, intl, isAllFiltersSelected]);

  const contractOffers = useMemo(
    () => offers.filter((offer) => offer.contract_id === contract.id),
    [offers, contract.id]
  );

  const serialMap = useMemo(() => computeOfferSerials(contractOffers), [
    contractOffers,
  ]);

  const handleBulkDownload = async () => {
    setIsBulkDownloading(true);

    // Activate the download progress modal
    useStore.getState().setIsOfferDownload(true);
    useStore.getState().changeDownloadingActive(true);
    useStore.getState().changeDownloadingState(DownloadingState.PREPARING);

    try {
      const offersData: BulkOfferEntry[] = contractOffers.map((offer) => ({
        offerId: offer.id,
        offerName: `${getOfferSerial(offer.id, serialMap)} ${getOfferVendorName(
          offer,
          intl
        )}`,
        offerSerial: getOfferSerial(offer.id, serialMap),
        mergedVouchers: buildMergedScreeningVouchers(
          vouchers,
          voucherResponsesByOfferId[offer.id] || [],
          intl
        ),
      }));
      await downloadAllOffersZip({
        projectId,
        tenderId,
        contractId: contract.id,
        contractName: contract.name || `Contract_${contract.id}`,
        offersData,
        tabLabel: 'Screening',
      });
    } catch {
      message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
    } finally {
      setIsBulkDownloading(false);
      // Modal will auto-close after FINISHING state (handled by saveFilesToZip)
    }
  };

  useEffect(() => {
    const nextState = contractOffers.reduce<
      Record<number, ScreeningEvaluationValue>
    >((acc, offer) => {
      acc[offer.id] = getOfferScreeningValue(offer);
      return acc;
    }, {});

    setScreeningStateByOfferId(nextState);
  }, [contractOffers]);

  const getOfferResponses = (offerId: number) => {
    return voucherResponsesByOfferId[offerId] || EMPTY_RESPONSES;
  };

  const handleToggleAllFilters = (checked: boolean) => {
    setFilterValues(checked ? DELIVERY_FILTER_VALUES : []);
  };

  const handleToggleFilter = (value: DeliveryFilterValue, checked: boolean) => {
    setFilterValues((prev) => {
      if (checked) {
        return prev.includes(value) ? prev : [...prev, value];
      }

      return prev.filter((filterValue) => filterValue !== value);
    });
  };

  const handleScreeningChange = useCallback(
    async (offerId: number, value: ScreeningEvaluationValue) => {
      const previousValue = screeningStateRef.current[offerId] ?? 0;

      if (previousValue === value) {
        return;
      }

      setScreeningStateByOfferId((prev) => ({
        ...prev,
        [offerId]: value,
      }));

      try {
        await setScreeningEvaluationMutation({ offerId, value });
      } catch (error) {
        setScreeningStateByOfferId((prev) => ({
          ...prev,
          [offerId]: previousValue,
        }));

        message.error(
          intl.formatMessage({
            id: 'screening-offer-decision-update-failed',
          })
        );
      }
    },
    [setScreeningEvaluationMutation, intl]
  );

  const filteredOffers = useMemo(() => {
    if (isAllFiltersSelected) {
      return contractOffers;
    }

    return contractOffers.filter((offer) => {
      const screeningValue = screeningStateByOfferId[offer.id] ?? 0;
      const deliveryFilterValue = getDeliveryFilterValue(screeningValue);

      return filterValues.includes(deliveryFilterValue);
    });
  }, [
    contractOffers,
    filterValues,
    isAllFiltersSelected,
    screeningStateByOfferId,
  ]);

  const summary = useMemo(() => {
    return contractOffers.reduce(
      (acc, offer) => {
        const screeningValue = screeningStateByOfferId[offer.id] ?? 0;

        acc.total += 1;

        if (offer.state === 4) {
          acc.delivered += 1;
        }

        if (screeningValue === 3) {
          acc.accepted += 1;
        } else if (screeningValue === 4) {
          acc.rejected += 1;
        } else {
          acc.unreviewed += 1;
        }

        return acc;
      },
      {
        total: 0,
        delivered: 0,
        unreviewed: 0,
        accepted: 0,
        rejected: 0,
      }
    );
  }, [contractOffers, screeningStateByOfferId]);

  const handleFinalizeScreening = () => {
    const undecidedCount = contractOffers.filter((offer) => {
      const screeningValue = screeningStateByOfferId[offer.id] ?? 0;

      return !isScreeningDecisionSelected(screeningValue);
    }).length;

    if (undecidedCount > 0) {
      Modal.warning({
        title: intl.formatMessage({
          id: 'screening-not-ready-modal-title',
        }),
        content: intl.formatMessage({
          id: 'screening-not-ready-modal-content',
        }),
      });

      return;
    }

    Modal.confirm({
      title: intl.formatMessage({
        id: 'screening-finalize-modal-title',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'screening-finalize-modal-content',
      }),
      okText: intl.formatMessage({ id: 'screening-finalize-button' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk: async () => {
        try {
          await finalizeScreeningMutation();

          message.success(
            intl.formatMessage({
              id: 'screening-finalize-success',
            })
          );
        } catch (error) {
          message.error(
            intl.formatMessage({
              id: 'screening-finalize-failed',
            })
          );
        }
      },
    });
  };

  const handleSkipScreening = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'screening-skip-modal-title',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'screening-skip-modal-content',
      }),
      okText: intl.formatMessage({ id: 'screening-skip-button' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk: async () => {
        try {
          await skipScreeningMutation();

          message.success(
            intl.formatMessage({
              id: 'screening-skip-success',
            })
          );
        } catch (error) {
          message.error(
            intl.formatMessage({
              id: 'screening-skip-failed',
            })
          );
        }
      },
    });
  };

  const handleAcceptAll = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'screening-accept-all-modal-title',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'screening-accept-all-modal-content',
      }),
      okText: intl.formatMessage({ id: 'screening-accept-all-button' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk: async () => {
        try {
          await acceptAllScreeningOffersMutation();

          const nextState = contractOffers.reduce<
            Record<number, ScreeningEvaluationValue>
          >((acc, offer) => {
            acc[offer.id] = 3;
            return acc;
          }, {});

          setScreeningStateByOfferId((prev) => ({
            ...prev,
            ...nextState,
          }));

          message.success(
            intl.formatMessage({
              id: 'screening-accept-all-success',
            })
          );
        } catch (error) {
          message.error(
            intl.formatMessage({
              id: 'screening-accept-all-failed',
            })
          );
        }
      },
    });
  };

  const filterContent = (
    <div css={styles.filterWrapper}>
      <div css={styles.filterItemWrapper}>
        <div css={styles.filterIconWrapper}>
          <FilterOutlined css={styles.filterAllIcon} />
          <span>
            {intl.formatMessage({ id: 'screening-filter-all-deliveries' })}
          </span>
        </div>

        <Checkbox
          checked={isAllFiltersSelected}
          onChange={(e) => handleToggleAllFilters(e.target.checked)}
        />
      </div>

      {SCREENING_DELIVERY_FILTER_OPTIONS.map((option) => (
        <div css={styles.filterItemWrapper} key={option.value}>
          <div css={styles.filterIconWrapper}>
            <span
              css={[
                styles.filterStatusIcon,
                {
                  color: option.color,
                },
              ]}>
              {option.icon}
            </span>

            <span>{intl.formatMessage({ id: option.intlId })}</span>
          </div>

          <Checkbox
            checked={filterValues.includes(option.value)}
            onChange={(e) => handleToggleFilter(option.value, e.target.checked)}
          />
        </div>
      ))}
    </div>
  );

  const isEmpty = contractOffers.length === 0;

  return (
    <div css={styles.wrapper}>
      <div css={styles.header}>
        <div>
          <div css={styles.eyebrow}>
            {intl.formatMessage({ id: 'screening-process-title' })}
          </div>
          <div css={styles.description}>
            {intl.formatMessage({ id: 'screening-process-description' })}
          </div>
        </div>

        <div css={styles.headerActions}>
          <Button
            disabled={!!isScreeningLocked || isScreeningActionLoading}
            loading={isSkippingScreening}
            onClick={handleSkipScreening}>
            {intl.formatMessage({ id: 'screening-skip-button' })}
          </Button>

          <Button
            disabled={!!isScreeningLocked || isScreeningActionLoading}
            loading={isAcceptingAllScreeningOffers}
            onClick={handleAcceptAll}>
            {intl.formatMessage({ id: 'screening-accept-all-button' })}
          </Button>

          <Button
            type="primary"
            disabled={!!isScreeningLocked || isScreeningActionLoading}
            loading={isFinalizingScreening}
            onClick={handleFinalizeScreening}>
            {intl.formatMessage({ id: 'screening-finalize-button' })}
          </Button>
        </div>
      </div>

      {isScreeningLocked && (
        <div css={styles.contractBlock}>
          <Tag color="orange">
            {intl.formatMessage({ id: 'screening-locked-read-only' })}
          </Tag>
        </div>
      )}

      <div css={styles.summaryGrid}>
        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.totalIcon]}>
            <ProfileOutlined />
          </div>
          <div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({ id: 'screening-summary-total' })}
            </div>
            <div css={styles.summaryValueLine}>
              <span css={styles.summaryValue}>{summary.total}</span>
              <span css={styles.summarySubValue}>
                {intl.formatMessage(
                  { id: 'screening-summary-delivered-value' },
                  { delivered: summary.delivered }
                )}
              </span>
            </div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.unreviewedIcon]}>
            <ClockCircleOutlined />
          </div>
          <div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({ id: 'screening-summary-unreviewed' })}
            </div>
            <div css={styles.summaryValue}>{summary.unreviewed}</div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.acceptedIcon]}>
            <CheckCircleOutlined />
          </div>
          <div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({ id: 'screening-summary-accepted' })}
            </div>
            <div css={styles.summaryValue}>{summary.accepted}</div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.rejectedIcon]}>
            <StopOutlined />
          </div>
          <div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({ id: 'screening-summary-rejected' })}
            </div>
            <div css={styles.summaryValue}>{summary.rejected}</div>
          </div>
        </div>
      </div>

      <div css={styles.toolbar}>
        <div css={styles.toolbarTitle}>
          <FilterOutlined />
          <span>
            {intl.formatMessage({ id: 'screening-filter-deliveries-title' })}
          </span>
        </div>

        <Popover placement="bottomLeft" content={filterContent} trigger="click">
          <Button size="small" css={styles.filterButton}>
            <FilterOutlined />
            <span css={styles.filterButtonText}>{filterButtonLabel}</span>
          </Button>
        </Popover>

        <Tooltip
          title={intl.formatMessage({ id: 'offers-bulk-download-tooltip' })}>
          <Button
            size="small"
            css={styles.downloadAllButton}
            icon={<DownloadOutlined />}
            loading={isBulkDownloading}
            onClick={handleBulkDownload}>
            {intl.formatMessage({ id: 'offers-bulk-download-label' })}
          </Button>
        </Tooltip>

        <div css={styles.toolbarCounter}>
          {intl.formatMessage(
            { id: 'screening-showing-counter' },
            {
              current: filteredOffers.length,
              total: contractOffers.length,
            }
          )}
        </div>
      </div>

      {isEmpty ? (
        <div css={styles.emptyBlock}>
          <Empty
            description={intl.formatMessage({
              id: 'screening-empty-deliveries',
            })}
          />
        </div>
      ) : (
        <div css={styles.list}>
          {filteredOffers.map((offer) => (
            <OffersScreeningRow
              projectId={projectId}
              tenderId={tenderId}
              contractId={contract.id}
              contractName={contract.name || ''}
              key={offer.id}
              offer={offer}
              serial={getOfferSerial(offer.id, serialMap)}
              vouchers={vouchers}
              responses={getOfferResponses(offer.id)}
              offerData={offerDataById?.[offer.id]}
              screeningValue={screeningStateByOfferId[offer.id] ?? 0}
              onScreeningChange={handleScreeningChange}
              isScreeningLocked={isScreeningLocked || isScreeningActionLoading}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingTop: 20,
    width: '100%',
  },

  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24,
  },

  eyebrow: {
    color: '#1890ff',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
  },

  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
    color: '#222222',
  },

  description: {
    marginTop: 10,
    maxWidth: 760,
    color: '#666666',
    fontSize: 14,
    lineHeight: 1.6,
  },

  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },

  infoAlert: {
    marginTop: 4,
  },

  contractBlock: {
    padding: 16,
    border: '1px solid #e8e8e8',
    borderRadius: 4,
    background: '#fafafa',
  },

  contractTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#222222',
  },

  contractDescription: {
    marginTop: 6,
    color: '#666666',
    fontSize: 13,
  },

  contractMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 10,
  },

  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(140px, 1fr))',
    gap: 12,
  },

  summaryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    border: '1px solid #e8e8e8',
    borderRadius: 4,
    background: '#ffffff',
  },

  summaryIcon: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
  },

  totalIcon: {
    background: '#e6f7ff',
    color: '#1890ff',
  },

  unreviewedIcon: {
    background: '#f5f5f5',
    color: '#8c8c8c',
  },

  acceptedIcon: {
    background: '#f6ffed',
    color: '#52c41a',
  },

  rejectedIcon: {
    background: '#fff1f0',
    color: '#ff4d4f',
  },

  summaryValueLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    flexWrap: 'wrap',
  },

  summarySubValue: {
    color: '#777777',
    fontSize: 12,
    fontWeight: 600,
  },

  summaryValue: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    color: '#222222',
  },

  summaryLabel: {
    marginTop: 4,
    color: '#777777',
    fontSize: 12,
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    border: '1px solid #e8e8e8',
    borderRadius: 4,
    background: '#ffffff',
  },

  toolbarTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 700,
    color: '#333333',
  },

  filterButton: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: 230,
    minWidth: 142,
    height: 28,
    padding: '0 10px',
  },

  filterButtonText: {
    display: 'inline-block',
    marginLeft: 6,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  downloadAllButton: {
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '0 10px',
    whiteSpace: 'nowrap',
  },

  filterWrapper: {
    minWidth: 178,
    padding: '6px 0',
  },

  filterItemWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
    padding: '6px 10px',
    color: '#222222',
    fontSize: 14,
    lineHeight: 1.2,

    '&:hover': {
      background: '#fafafa',
    },

    '.ant-checkbox-wrapper': {
      marginLeft: 0,
    },
  },

  filterIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    whiteSpace: 'nowrap',
  },

  filterAllIcon: {
    color: '#1890ff',
    fontSize: 14,
  },

  filterStatusIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 14,
    minWidth: 14,
    fontSize: 14,
  },

  toolbarCounter: {
    marginLeft: 'auto',
    color: '#777777',
    fontSize: 12,
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },

  emptyBlock: {
    padding: '40px 0',
    border: '1px solid #e8e8e8',
    borderRadius: 4,
    background: '#ffffff',
  },
});
